// Form-level validation for the question edit panel (PRD 05 §8.1 + §8.4).
//
// Two-tier validation:
//   - `getRequiredFieldErrorCode` runs on every keystroke and drives the Save
//     button's disabled state (PRD §3.7).
//   - `validateOnSave` runs at click time and catches range / config errors
//     that don't block the Save button but do block the actual save.
//
// Both return one of the codes from `QuestionDraftErrorCode`, or null. The
// matching translated message lives at `validation.<code>`
// in admin.yaml — the calling component does the translation so the pure
// helpers stay React-free.

import { hasEmoji } from '@/shared/utils/emoji';

import type {
  BatteryConfig,
  Choice,
  CreateQuestionInput,
  PhotoConfig,
  Question,
  ScoringAdvanced,
  SliderConfig,
  TextConfig,
  UpdateQuestionInput,
} from '../types/questionnaire-design.types';

// Mirrors the keys under `validation:` in admin.yaml.
export type QuestionDraftErrorCode =
  | 'textRequired'
  | 'metaRequired'
  | 'diseaseGroupRequired'
  | 'diseaseCodeRequired'
  | 'choiceTextRequired'
  | 'minChoices'
  | 'otherChoiceLimit'
  | 'weightOutOfRange'
  | 'reliabilityOutOfRange'
  | 'branchCycle'
  | 'maxDepthExceeded'
  | 'textConfigInvalid'
  | 'textMinCharsRange'
  | 'textMaxCharsOver'
  | 'textMaxCharsRange'
  | 'textScoreRequired'
  | 'textScoreRange'
  | 'sliderConfigInvalid'
  | 'sliderStepNotPositive'
  | 'sliderStepInvalid'
  | 'sliderUnitInvalid'
  | 'photoConfigInvalid'
  | 'photoMaxSizeInvalid'
  | 'batteryEmpty'
  | 'scaleLabelInvalid'
  | 'categoryNameInvalid'
  | 'batteryItemTextInvalid'
  | 'choiceDuplicate'
  | 'scaleLabelDuplicate'
  | 'categoryNameDuplicate'
  | 'batteryItemTextDuplicate'
  | 'answerScoreOutOfRange'
  | 'emojiNotAllowed';

// SHCT-436 — duplicate answer options. No spec defines this rule (PRD 05 §3.3 /
// §8.1 / §8.4, [공통] 00_입력검증표준 and both clarification logs are all silent,
// and the prototype allows duplicates), so the comparison rule below is the
// decision recorded on the ticket: compare on trim + case-insensitive, and the
// fixed-text 기타 (텍스트입력) choice is excluded (it is already limited to one
// per question). Message copy follows PRD 21 §7.2 `이미 등록된 [항목명]입니다.`
function normalizeForDuplicate(value: string): string {
  return (value ?? '').trim().toLowerCase();
}

function hasDuplicate(values: string[]): boolean {
  const seen = new Set<string>();
  for (const value of values) {
    const key = normalizeForDuplicate(value);
    if (seen.has(key)) return true;
    seen.add(key);
  }
  return false;
}

// Duplicate check for the choice table — 단답형 / 중복선택형 only. Yes/No and OX
// carry fixed text, so they cannot duplicate. Runs AFTER the empty-text check so
// two blank rows report `choiceTextRequired` rather than "duplicate".
function duplicateChoiceErrorCode(
  ansType: Question['answerType'] | undefined,
  choices: Choice[],
): QuestionDraftErrorCode | null {
  if (ansType !== 'SHORT_ANSWER' && ansType !== 'MULTI_CHOICE') return null;
  const texts = choices.filter((c) => c.chType !== 'other').map((c) => c.text ?? '');
  return hasDuplicate(texts) ? 'choiceDuplicate' : null;
}

// PRD 05 §8.4 + clarification ADM.041 (2026-07-16, SHCT-434) — battery (묶음
// 응답형) content validation. Beyond the ≥1-category / ≥1-item count rule, ADM.041
// requires each scale label 1~20 chars, each category name 1~20, each item text
// 1~50. Whitespace-only counts as empty (trim) per [공통] 00_입력검증표준.
const SCALE_LABEL_MAX = 20;
const CATEGORY_NAME_MAX = 20;
const BATTERY_ITEM_TEXT_MAX = 50;

function batteryErrorCode(cfg: BatteryConfig | undefined): QuestionDraftErrorCode | null {
  if (!cfg || cfg.categories.length === 0) return 'batteryEmpty';
  for (const label of cfg.scaleLabels) {
    const v = label.trim();
    if (v.length < 1 || v.length > SCALE_LABEL_MAX) return 'scaleLabelInvalid';
  }
  for (const cat of cfg.categories) {
    const name = cat.name.trim();
    if (name.length < 1 || name.length > CATEGORY_NAME_MAX) return 'categoryNameInvalid';
    if (cat.items.length === 0) return 'batteryEmpty';
    for (const item of cat.items) {
      const text = item.text.trim();
      if (text.length < 1 || text.length > BATTERY_ITEM_TEXT_MAX) return 'batteryItemTextInvalid';
    }
  }
  // SHCT-436 — duplicates, checked after the length rules so a blank entry
  // reports its own message first. Scope: scale labels and category names must
  // be unique across the question; item text must be unique WITHIN its own
  // category (two categories legitimately reusing the same item wording — e.g.
  // '매일 실천한다' under both 운동관리 and 식이관리 — stays allowed).
  if (hasDuplicate(cfg.scaleLabels)) return 'scaleLabelDuplicate';
  if (hasDuplicate(cfg.categories.map((c) => c.name))) return 'categoryNameDuplicate';
  for (const cat of cfg.categories) {
    if (hasDuplicate(cat.items.map((i) => i.text))) return 'batteryItemTextDuplicate';
  }
  return null;
}

// SHCT-498 / US-QD-CR03 (clarification ADM.041, 2026-07-16) — the per-type
// config limits PRD 05 §8.4 left undefined. §8.4 only had the relational checks
// (minChars > maxChars, min ≥ max, maxFiles/maxSizeMB < 1); ADM.041 adds the
// absolute bounds below. SHCT-439 is the maxChars ≤ 500 half of this CR.
//
// textConfig  : minChars 1~500, maxChars 1~500 (ADM.041 "maxChars: max 500";
//               PRD 05 §textConfig defaults 1 / 500), score 0~100 integer,
//               required (ADM.041 "score: number, 0 ~ 100, positive, required")
// sliderConfig: step ≤ max / 10 (ADM.041 "최대값의 1/10을 max로"), unit 1~10 chars
// photoConfig : maxSizeMB ≤ 5 (ADM.041 "maxSize: 5MB"); maxFiles 1~9 is PRD 05
//               §8.4 / §photoConfig ("파일 수는 1~9") — previously only `< 1` was
//               checked, so the documented upper bound is enforced here too.
const TEXT_CHARS_MIN = 1;
const TEXT_CHARS_MAX = 500;
const TEXT_SCORE_MIN = 0;
const TEXT_SCORE_MAX = 100;
const SLIDER_STEP_MAX_RATIO = 10; // step ≤ max ÷ 10
const SLIDER_UNIT_MIN = 1;
const SLIDER_UNIT_MAX = 10;
const PHOTO_MAX_FILES_MIN = 1;
const PHOTO_MAX_FILES_MAX = 9;
const PHOTO_MAX_SIZE_MB_MIN = 1;
const PHOTO_MAX_SIZE_MB_MAX = 5;

function textConfigErrorCode(cfg: TextConfig | undefined): QuestionDraftErrorCode | null {
  if (!cfg) return 'metaRequired';
  const min = Number(cfg.minChars);
  const max = Number(cfg.maxChars);
  // Character counts are whole numbers, so a fractional entry is rejected too.
  if (!Number.isInteger(min) || min < TEXT_CHARS_MIN || min > TEXT_CHARS_MAX) {
    return 'textMinCharsRange';
  }
  // SHCT-498 / ADM.041 — the two halves of this bound get DIFFERENT messages. PRD 05 §8.4 words only the
  // over-bound case, and answering it for a value that is too SMALL points the person in the opposite
  // direction from the fix. Mirrors the server split exactly: the two sides must refuse the same inputs with
  // the same sentences, or the message a user sees depends on which layer happened to catch them first.
  if (Number.isInteger(max) && max > TEXT_CHARS_MAX) {
    return 'textMaxCharsOver';
  }
  if (!Number.isInteger(max) || max < TEXT_CHARS_MIN) {
    // A non-integer maxChars folds in here rather than into the over-bound message: 12.5 is not "too large",
    // and the range sentence is the one that tells the person what shape the value must take.
    return 'textMaxCharsRange';
  }
  // PRD 05 §8.4 relational rule — kept, and still reported with its own message.
  if (min > max) return 'textConfigInvalid';
  // SHCT-498 / ADM.041 — a MISSING score and an OUT-OF-RANGE score are separate rows in PRD 05 §8.4, so
  // somebody who simply left the field empty is no longer told about a range they never entered.
  if (cfg.score === undefined || cfg.score === null || String(cfg.score).trim() === '') {
    return 'textScoreRequired';
  }
  const score = Number(cfg.score);
  if (!Number.isInteger(score) || score < TEXT_SCORE_MIN || score > TEXT_SCORE_MAX) {
    return 'textScoreRange';
  }
  return null;
}

function sliderConfigErrorCode(cfg: SliderConfig | undefined): QuestionDraftErrorCode | null {
  if (!cfg) return 'metaRequired';
  if (cfg.min >= cfg.max) return 'sliderConfigInvalid';
  const step = Number(cfg.step);
  const stepLimit = Number(cfg.max) / SLIDER_STEP_MAX_RATIO;
  // Float-safe compare — a 1-dp step against a /10 limit (e.g. 0.3 vs 3/10)
  // can land a hair over the bound in binary floating point.
  // SHCT-498 / ADM.041 — split for the same reason as maxChars above. The 1e-9 tolerance stays exactly as it
  // was: it is what stops a step that is EXACTLY one tenth of the maximum being rejected by binary rounding.
  if (!Number.isFinite(step) || step <= 0) {
    return 'sliderStepNotPositive';
  }
  if (step - stepLimit > 1e-9) {
    return 'sliderStepInvalid';
  }
  const unit = (cfg.unit ?? '').trim();
  if (unit.length < SLIDER_UNIT_MIN || unit.length > SLIDER_UNIT_MAX) {
    return 'sliderUnitInvalid';
  }
  return null;
}

function photoConfigErrorCode(cfg: PhotoConfig | undefined): QuestionDraftErrorCode | null {
  if (!cfg) return 'metaRequired';
  const maxFiles = Number(cfg.maxFiles);
  if (
    !Number.isInteger(maxFiles) ||
    maxFiles < PHOTO_MAX_FILES_MIN ||
    maxFiles > PHOTO_MAX_FILES_MAX
  ) {
    return 'photoConfigInvalid';
  }
  // maxSizeMB is not constrained to whole MB by ADM.041, only to a 5MB ceiling.
  const maxSizeMB = Number(cfg.maxSizeMB);
  if (
    !Number.isFinite(maxSizeMB) ||
    maxSizeMB < PHOTO_MAX_SIZE_MB_MIN ||
    maxSizeMB > PHOTO_MAX_SIZE_MB_MAX
  ) {
    return 'photoMaxSizeInvalid';
  }
  return null;
}

// PRD 05 §8.1 — reliability (cᵢ) range/step check. Per SHCT-247 BR-Rel-01 an
// out-of-range reliability is a NON-BLOCKING warning (the input restores the
// previous value + shows a warning), so it is intentionally NOT part of
// `validateOnSave`. The scoring section calls this to drive the inline warning.
export function isReliabilityValid(reliability: number): boolean {
  const r = Number(reliability);
  const rRounded = Math.round(r * 10) / 10;
  return Number.isFinite(r) && r >= 0 && r <= 1 && Math.abs(r - rRounded) <= 1e-9;
}

// Per the edit-panel UX, a draft is the union of fields the form binds plus
// the optional configs. The slide-panel keeps an in-memory `draft` object and
// pipes it into both validators + the final create/update payload.
export interface QuestionDraft extends CreateQuestionInput {}

export function getRequiredFieldErrorCode(
  draft: Partial<QuestionDraft>,
): QuestionDraftErrorCode | null {
  if (!draft.text || !draft.text.trim()) return 'textRequired';
  if (!draft.target || !draft.gender || !draft.answerType) {
    return 'metaRequired';
  }
  // SHCT-517 / [ADM.046] — 적용 범위는 문항의 입력값이 아니라 소속 항목 Scope의 상속값이다.
  // 저장 직전 항목 값으로 채워지므로 문항 단위 필수 검증을 두면 사용자가 고칠 수 없는 필드에서
  // 저장이 막힌다. 항목 Scope 자체의 필수 검증은 PRD 04 항목 폼이 담당한다.
  return null;
}

// SHCT-853 — [공통] 00_입력검증표준 §4.2: 이모지는 전 클래스 불허, 차단은 인라인 에러.
// The rule is a property of every user-authored string, so rather than bolting a
// check onto each field's own range rule this walks the whole draft once. Numeric
// config (scores, char counts, file limits) and branch targets carry no free text.
// Split in two so `collectFieldErrors` can attach each half to the field the
// user actually has to fix — the question textarea, or the answer area.
function answerAreaEmojiErrorCode(draft: QuestionDraft): QuestionDraftErrorCode | null {
  const texts: (string | undefined)[] = [];
  for (const c of draft.choices ?? []) texts.push(c.text);
  texts.push(draft.sliderConfig?.unit);
  const battery = draft.batteryConfig;
  if (battery) {
    texts.push(...battery.scaleLabels);
    for (const cat of battery.categories) {
      texts.push(cat.name);
      for (const item of cat.items) texts.push(item.text);
    }
  }
  return texts.some(hasEmoji) ? 'emojiNotAllowed' : null;
}

// SHCT-851 — every answer score must sit inside the question's own 최저점~최고점
// (`advanced.min`/`advanced.max`, PRD 05 §3.9 / §4.2). No PRD clause states this
// today, so the range is the operator's decision on the ticket: symmetric
// `min ≤ score ≤ max`, blocking, applied to 선택지 점수 · textConfig.score ·
// 슬라이더 범위 · 묶음응답형 척도 점수.
//
// Why it matters beyond tidiness: the R engine normalises with
// nᵢ = (answer − min) / (max − min) (clarification ADM.031, mirrored by the
// backend RiskEngine), so a score outside the band yields nᵢ outside 0..1 and
// pushes the composite R off its 0.0–26.8 operating range (ADM.024).
//
// Skipped when the question is excluded from scoring (`scoreContribution=false`,
// §3.9.3): the four inputs are then disabled and preserved but take no part in
// the formula, so holding a save hostage to a disabled field would dead-end.
// The band predicate on its own, so the editor can red-ring the individual score
// inputs that fall outside it (SHCT-851 "Highlight invalid Answer Scores") using
// exactly the rule the save-time check applies. Returns a predicate that is
// always `false` when the band does not apply, so callers need no special case.
export function makeScoreOutOfBand(advanced: ScoringAdvanced): (score: unknown) => boolean {
  const { scoreContribution, min, max } = advanced;
  const lo = Number(min);
  const hi = Number(max);
  if (!scoreContribution || !Number.isFinite(lo) || !Number.isFinite(hi) || lo > hi) {
    return () => false;
  }
  return (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) && (n < lo || n > hi);
  };
}

function answerScoreRangeErrorCode(draft: QuestionDraft): QuestionDraftErrorCode | null {
  const outOfBand = makeScoreOutOfBand(draft.advanced);
  const inBand = (v: unknown): boolean => !outOfBand(v);

  const ansType = draft.answerType;
  // YES_NO stores its two scores in `choices` exactly like the other choice-based
  // types (see QuestionDraft.choices), so it belongs in the same branch. It was the
  // one type left out when the band check landed, which let an out-of-band Yes/No
  // score save and then fail at patient submit time.
  if (
    ansType === 'SHORT_ANSWER' ||
    ansType === 'MULTI_CHOICE' ||
    ansType === 'OX' ||
    ansType === 'YES_NO'
  ) {
    // 기타 (텍스트입력) carries a score like any other choice, so it is included.
    if (!(draft.choices ?? []).every((c) => inBand(c.score))) return 'answerScoreOutOfRange';
  }
  if (ansType === 'TEXT' && !inBand(draft.textConfig?.score)) {
    return 'answerScoreOutOfRange';
  }
  if (ansType === 'SLIDER') {
    // The patient's answer IS the slider value, so the whole selectable range has
    // to fit the band — both endpoints, not a single score.
    const cfg = draft.sliderConfig;
    if (cfg && (!inBand(cfg.min) || !inBand(cfg.max))) return 'answerScoreOutOfRange';
  }
  if (ansType === 'BATTERY' && !(draft.batteryConfig?.scaleScores ?? []).every(inBand)) {
    return 'answerScoreOutOfRange';
  }
  return null;
}

export function validateOnSave(
  draft: QuestionDraft,
): QuestionDraftErrorCode | null {
  const required = getRequiredFieldErrorCode(draft);
  if (required) return required;

  // §3.9.2 / §8.1 — weight range/step. Per clarification No 92: 0.1 ~ 10.0,
  // one decimal place (step 0.1), zero not allowed. Toggle OFF does not exempt
  // the value; it's preserved either way and the BE re-validates. Reliability is
  // handled separately as a non-blocking warning, so it does NOT block save here.
  const w = Number(draft.advanced.weight);
  const wRounded = Math.round(w * 10) / 10;
  if (!Number.isFinite(w) || w < 0.1 || w > 10.0 || Math.abs(w - wRounded) > 1e-9) {
    return 'weightOutOfRange';
  }

  // §3.3 — choice-text required for choice-based types, plus the §8.1 "기타"
  // single-instance limit. The "기타" label itself is fixed (PRD §3.3), so it is
  // skipped here; its empty/500-char rules apply to the patient's runtime
  // response (otherText), not admin authoring. YES_NO has fixed text.
  const ansType = draft.answerType;
  if (ansType === 'SHORT_ANSWER' || ansType === 'MULTI_CHOICE' || ansType === 'OX') {
    const choices = draft.choices ?? [];
    if (choices.length === 0) return 'choiceTextRequired';
    for (const c of choices) {
      if (c.chType !== 'other' && (!c.text || !c.text.trim())) {
        return 'choiceTextRequired';
      }
    }
    const otherCount = choices.filter((c) => c.chType === 'other').length;
    if (otherCount > 1) return 'otherChoiceLimit';
    const dup = duplicateChoiceErrorCode(ansType, choices);
    if (dup) return dup;
  }

  // §8.4 + ADM.041 type-specific config validation.
  if (ansType === 'TEXT') {
    const code = textConfigErrorCode(draft.textConfig);
    if (code) return code;
  }
  if (ansType === 'SLIDER') {
    const code = sliderConfigErrorCode(draft.sliderConfig);
    if (code) return code;
  }
  if (ansType === 'PHOTO') {
    const code = photoConfigErrorCode(draft.photoConfig);
    if (code) return code;
  }
  if (ansType === 'BATTERY') {
    const code = batteryErrorCode(draft.batteryConfig);
    if (code) return code;
  }

  // Run last: the per-field range/required rules give the more specific message,
  // so these two only speak up once the draft is otherwise well-formed.
  if (hasEmoji(draft.text)) return 'emojiNotAllowed';
  const emoji = answerAreaEmojiErrorCode(draft);
  if (emoji) return emoji;

  return answerScoreRangeErrorCode(draft);
}

// PRD 05 §8.2 — branch cycle / backward-jump check. Runs at save time once
// the draft + sibling questions are known. Rules borrowed from the prototype:
//
//   - JUMP target cannot be the question itself.
//   - JUMP target must be either (a) a forward same-(itemId, target, parentQid)
//     sibling — `order` strictly greater than the draft's own order — or
//     (b) one of the draft's OWN sub-questions when the draft is a main question
//     (SHCT-435, prototype buildBranchOpts `currentQ.children`). Own sub-questions
//     live in a different (parentQid) bucket but always render after the parent,
//     so they are structurally forward and can never form a cycle (a sub-question
//     may only jump to a sibling sub, never back up to its parent).
//   - For a new question (no existing qid) the draft is treated as appended at
//     the end of its bucket and has no saved sub-questions yet, so every JUMP
//     lands in the past → rejected. This mirrors the prototype's intent: a new
//     node cannot jump backward.
//
// Forward-only jumps make a cycle structurally impossible, so this FE check
// already covers the §5.5 ancestor-cycle case. It is, however, only
// defense-in-depth: the BE re-validates branch destination + cycle
// authoritatively (QuestionService.validateBranches →
// BRANCH_TARGET_INVALID / BRANCH_CYCLE), because a direct API call bypasses the FE.
export function validateBranches(
  draft: QuestionDraft,
  existingQid: string | null,
  allQuestionsInItem: Question[],
): QuestionDraftErrorCode | null {
  const choices = draft.choices ?? [];
  if (choices.length === 0) return null;

  const siblings = allQuestionsInItem.filter(
    (q) =>
      q.target === draft.target &&
      (q.parentQid ?? null) === (draft.parentQid ?? null),
  );
  const myOrder = existingQid
    ? siblings.find((q) => q.qid === existingQid)?.order ?? siblings.length
    : siblings.length;
  const orderByQid = new Map<string, number>();
  for (const sib of siblings) orderByQid.set(sib.qid, sib.order);

  // Own sub-questions — valid forward targets only when the draft is an existing
  // main question (top-level, parentQid == null).
  const ownSubQids = new Set<string>(
    existingQid && (draft.parentQid ?? null) === null
      ? allQuestionsInItem
          .filter((q) => q.target === draft.target && q.parentQid === existingQid)
          .map((q) => q.qid)
      : [],
  );

  for (const choice of choices) {
    if (choice.branch?.kind !== 'JUMP') continue;
    const targetQid = choice.branch.targetQid;
    if (!targetQid || targetQid === existingQid) return 'branchCycle';
    if (ownSubQids.has(targetQid)) continue; // parent → own sub: forward, acyclic
    const targetOrder = orderByQid.get(targetQid);
    if (targetOrder === undefined || targetOrder <= myOrder) return 'branchCycle';
  }
  return null;
}

// ── Field-keyed validation (SHCT-421 #1) ────────────────────────────────
//
// The slide panel renders each validation error INLINE under its own field
// (§7.1 — Validation Error → inline red text), so it needs errors keyed by
// field rather than a single first-error code. `collectFieldErrors` runs every
// check without short-circuiting and returns every currently-failing field, so
// all validation errors surface at once instead of one at a time.

export type QuestionFieldErrorKey =
  | 'text'
  | 'answerType'
  | 'gender'
  | 'applicability'
  | 'diseaseGroup'
  | 'diseaseCode'
  | 'answerArea'
  | 'weight';

export type QuestionFieldErrors = Partial<
  Record<QuestionFieldErrorKey, QuestionDraftErrorCode>
>;

// The answer-area (choices / per-type config / branch) portion of the save
// check, scoped so its error can be attached to the answer-area field. Runs
// only once a type is picked.
function answerAreaErrorCode(
  draft: QuestionDraft,
  existingQid: string | null,
  allQuestionsInItem: Question[],
): QuestionDraftErrorCode | null {
  const ansType = draft.answerType;
  if (ansType === 'SHORT_ANSWER' || ansType === 'MULTI_CHOICE' || ansType === 'OX') {
    const choices = draft.choices ?? [];
    // SHCT-433 — 단답형(single-select) needs ≥2 choices (prototype "라디오 선택 방식
    // (최소 2개)"). 기타 counts toward the total. OX has fixed 2; MULTI_CHOICE not scoped.
    if (ansType === 'SHORT_ANSWER' && choices.length < 2) return 'minChoices';
    if (choices.length === 0) return 'choiceTextRequired';
    for (const c of choices) {
      if (c.chType !== 'other' && (!c.text || !c.text.trim())) {
        return 'choiceTextRequired';
      }
    }
    if (choices.filter((c) => c.chType === 'other').length > 1) {
      return 'otherChoiceLimit';
    }
    const dup = duplicateChoiceErrorCode(ansType, choices);
    if (dup) return dup;
  }
  if (ansType === 'TEXT') {
    const code = textConfigErrorCode(draft.textConfig);
    if (code) return code;
  }
  if (ansType === 'SLIDER') {
    const code = sliderConfigErrorCode(draft.sliderConfig);
    if (code) return code;
  }
  if (ansType === 'PHOTO') {
    const code = photoConfigErrorCode(draft.photoConfig);
    if (code) return code;
  }
  if (ansType === 'BATTERY') {
    const code = batteryErrorCode(draft.batteryConfig);
    if (code) return code;
  }
  const emoji = answerAreaEmojiErrorCode(draft);
  if (emoji) return emoji;
  const scoreBand = answerScoreRangeErrorCode(draft);
  if (scoreBand) return scoreBand;
  return validateBranches(draft, existingQid, allQuestionsInItem);
}

// Runs the full save-time validation and returns every failing field at once.
// An empty object means the draft is valid.
//
// SHCT-438 — `gender` and 적용 범위 no longer always arrive filled: a sub-question now opens
// with both empty ([ADM.048] · [ADM.060] Q2), so each reports on its own field instead of
// pushing the operator to the answer-type select to read why the save was refused.
export function collectFieldErrors(
  draft: QuestionDraft,
  existingQid: string | null,
  allQuestionsInItem: Question[],
): QuestionFieldErrors {
  const errors: QuestionFieldErrors = {};

  if (!draft.text || !draft.text.trim()) errors.text = 'textRequired';
  else if (hasEmoji(draft.text)) errors.text = 'emojiNotAllowed';
  if (!draft.target || !draft.answerType) errors.answerType = 'metaRequired';
  if (!draft.gender) errors.gender = 'metaRequired';
  // SHCT-517 / [ADM.046] — 적용 범위·질병군·질병코드는 항목 Scope 상속값(읽기 전용 표시)이라
  // 문항 단위 검증 대상이 아니다. 위 getRequiredFieldErrorCode 주석 참조.

  const w = Number(draft.advanced.weight);
  const wRounded = Math.round(w * 10) / 10;
  if (!Number.isFinite(w) || w < 0.1 || w > 10.0 || Math.abs(w - wRounded) > 1e-9) {
    errors.weight = 'weightOutOfRange';
  }

  if (draft.answerType) {
    const areaCode = answerAreaErrorCode(draft, existingQid, allQuestionsInItem);
    if (areaCode) errors.answerArea = areaCode;
  }

  return errors;
}

// Helper to convert a full draft into the wire-format Update payload — drops
// the per-type configs that don't match the current answer type so the BE
// never receives stale subconfigs (e.g. a textConfig when answerType=SLIDER).
export function draftToUpdateInput(draft: QuestionDraft): UpdateQuestionInput {
  const base: UpdateQuestionInput = {
    text: draft.text,
    target: draft.target,
    gender: draft.gender,
    applicability: draft.applicability,
    diseaseGroupId: draft.applicability === 'DISEASE_GROUP' ? draft.diseaseGroupId : null,
    diseaseCodeId: draft.applicability === 'DISEASE_SPECIFIC' ? draft.diseaseCodeId : null,
    answerType: draft.answerType,
    allowPhoto: draft.allowPhoto,
    advanced: draft.advanced,
  };
  switch (draft.answerType) {
    case 'MULTI_CHOICE':
      base.choices = draft.choices;
      base.multiConfig = draft.multiConfig;
      break;
    case 'SHORT_ANSWER':
    case 'YES_NO':
    case 'OX':
      base.choices = draft.choices;
      break;
    case 'TEXT':
      base.textConfig = draft.textConfig;
      break;
    case 'SLIDER':
      base.sliderConfig = draft.sliderConfig;
      break;
    case 'PHOTO':
      base.photoConfig = draft.photoConfig;
      break;
    case 'BATTERY':
      base.batteryConfig = draft.batteryConfig;
      break;
  }
  return base;
}
